import axios from 'axios'
import type { Artist, Release, BandcampScrapingResult, PromptSession } from '../types'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
})

export const artistService = {
  getAll: async (): Promise<Artist[]> => {
    const response = await api.get('/artists')
    return response.data
  },

  getById: async (id: string): Promise<Artist> => {
    const response = await api.get(`/artists/${id}`)
    return response.data
  },

  create: async (artist: Omit<Artist, 'id' | 'createdAt'>): Promise<Artist> => {
    const response = await api.post('/artists', artist)
    return response.data
  },

  update: async (id: string, artist: Partial<Artist>): Promise<Artist> => {
    const response = await api.put(`/artists/${id}`, artist)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/artists/${id}`)
  },
}

export const releaseService = {
  getAll: async (): Promise<Release[]> => {
    const response = await api.get('/releases')
    return response.data
  },

  getById: async (id: string): Promise<Release> => {
    const response = await api.get(`/releases/${id}`)
    return response.data
  },

  getByArtist: async (artistId: string): Promise<Release[]> => {
    const response = await api.get(`/releases/artist/${artistId}`)
    return response.data
  },

  create: async (release: Omit<Release, 'id' | 'createdAt' | 'artistName'>): Promise<Release> => {
    const response = await api.post('/releases', release)
    return response.data
  },

  update: async (id: string, release: Partial<Release>): Promise<Release> => {
    const response = await api.put(`/releases/${id}`, release)
    return response.data
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/releases/${id}`)
  },
}

export const adminService = {
  triggerBandcampScrape: async (): Promise<BandcampScrapingResult> => {
    const response = await api.post('/admin/scrape-bandcamp')
    return response.data
  },
}

export const promptSessionService = {
  getAll: async (): Promise<PromptSession[]> => {
    const response = await api.get('/admin/ai-prompt/sessions')
    return response.data
  },

  getById: async (id: number): Promise<PromptSession> => {
    const response = await api.get(`/admin/ai-prompt/sessions/${id}`)
    return response.data
  },

  create: async (prompt: string): Promise<PromptSession> => {
    const response = await api.post('/admin/ai-prompt/sessions', { prompt })
    return response.data
  },

  approve: async (id: number): Promise<PromptSession> => {
    const response = await api.post(`/admin/ai-prompt/sessions/${id}/approve`)
    return response.data
  },

  reject: async (id: number): Promise<PromptSession> => {
    const response = await api.post(`/admin/ai-prompt/sessions/${id}/reject`)
    return response.data
  },

  cancel: async (id: number): Promise<PromptSession> => {
    const response = await api.delete(`/admin/ai-prompt/sessions/${id}`)
    return response.data
  },
}
